// Color utility functions for consistent color assignment across charts
// This ensures unique colors are used for vendors and categories across the dashboard

// Base color palette with high contrast and good visibility
// Colors for pie charts and stack plots: Red, Blue, Green, Yellow, Orange, Purple, Cyan, Brown, Pink, Grey, Peach, Teal
export const BASE_COLORS = [
  '#E6194B', '#0082C8', '#3CB44B', '#FFE119', '#F58231', 
  '#911EB4', '#46F0F0', '#9A6324', '#FABED4', '#808080',
  '#FFDAB9', '#008080'
];

// Blue color for bar charts
export const BAR_CHART_COLOR = '#0082C8';

// Extended color palette to handle more unique items
const EXTENDED_COLORS = [
  '#E6194B', '#0082C8', '#3CB44B', '#FFE119', '#F58231', 
  '#911EB4', '#46F0F0', '#9A6324', '#FABED4', '#808080',
  '#FFDAB9', '#008080', '#800000', '#A0522D', '#00FF00',
  '#0000FF', '#FF00FF', '#808000', '#008080', '#FF69B4',
  '#4B0082', '#FF4500', '#2E8B57', '#DC143C', '#7CFC00',
  '#00FA9A', '#9370DB', '#48D1CC', '#FF1493', '#00BFFF',
  '#FFD700', '#B22222', '#8B4513', '#20B2AA', '#00008B'
];

// Function to generate a hex color from HSL values
const hslToHex = (h, s, l) => {
  l /= 100;
  const a = s * Math.min(l, 1 - l) / 100;
  const f = n => {
    const k = (n + h / 30) % 12;
    const color = l - a * Math.max(Math.min(k - 3, 9 - k, 1), -1);
    return Math.round(255 * color).toString(16).padStart(2, '0');
  };
  return `#${f(0)}${f(8)}${f(4)}`;
};

// Function to generate unique colors dynamically if we run out of predefined colors
const generateUniqueColor = (index, totalItems) => {
  if (index < EXTENDED_COLORS.length) {
    return EXTENDED_COLORS[index];
  }
  
  // Generate colors using HSL for better distribution
  const hue = (index * 137.508) % 360; // Golden angle for good color distribution
  const saturation = 70 + (index % 30); // Vary saturation slightly
  const lightness = 40 + (index % 20);  // Vary lightness slightly
  
  return hslToHex(hue, saturation, lightness);
};

// Function to get unique colors for all items, ensuring no duplicates
export const getUniqueColors = (items) => {
  const colors = [];
  
  items.forEach((item, index) => {
    const color = generateUniqueColor(index, items.length);
    colors.push(color);
  });
  
  return colors;
};

// Function to get color for specific chart type with unique colors
export const getColorForChartType = (name, chartType = 'default', items = [], index = 0) => {
  if (chartType === 'bar') {
    // For regular bar charts, use a single color
    return BAR_CHART_COLOR;
  } else if (chartType === 'stacked' || chartType === 'pie') {
    // For stacked charts and pie charts, generate unique colors based on the name
    // Sort items to ensure consistent ordering
    const sortedItems = [...items].sort((a, b) => {
      const nameA = typeof a === 'string' ? a : a.name;
      const nameB = typeof b === 'string' ? b : b.name;
      return nameA.localeCompare(nameB);
    });
    
    // Find the index of this name in the sorted items array to ensure consistent color assignment
    const foundIndex = sortedItems.findIndex(item => {
      const itemName = typeof item === 'string' ? item : item.name;
      return itemName === name;
    });
    
    return generateUniqueColor(foundIndex !== -1 ? foundIndex : index, sortedItems.length);
  } else {
    // For other chart types, generate unique colors
    return generateUniqueColor(index, items.length);
  }
};

// Function to ensure consistent color assignment across chart instances
export const getColorForName = (name, items = [], index = 0) => {
  // Sort items to ensure consistent ordering for consistent color assignment
  const sortedItems = [...items].sort((a, b) => {
    const nameA = typeof a === 'string' ? a : a.name;
    const nameB = typeof b === 'string' ? b : b.name;
    return nameA.localeCompare(nameB);
  });
  
  // Find the index of this name in the sorted items array to ensure consistent color assignment
  const foundIndex = sortedItems.findIndex(item => {
    const itemName = typeof item === 'string' ? item : item.name;
    return itemName === name;
  });
  
  return generateUniqueColor(foundIndex !== -1 ? foundIndex : index, sortedItems.length);
};